import { distinctUntilChanged, filter, map, startWith, switchMap, shareReplay } from 'rxjs/operators';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SpeciesService } from 'src/app/core/services/species.service';
import { ChangeDetectionStrategy, Component, forwardRef, OnInit, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { isNil, isString, startCase, toLower } from 'lodash';
import { untilDestroy } from '@ngrx-utils/store';
import { Species } from 'src/app/core/models/species.model';

@Component({
  selector: 'app-species-form-field',
  templateUrl: './species-form-field.component.html',
  styleUrls: ['./species-form-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SpeciesFormFieldComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpeciesFormFieldComponent implements OnInit, OnDestroy {

  input = new FormControl();

  species$ = this.speciesService.species$;
  filteredSpecies$ = this.getFilteredSpecies();
  showRemove$ = new BehaviorSubject<string | null>(null);
  showAddSpecies$ = this.getShowAddSpecies();

  constructor(
    private speciesService: SpeciesService
  ) { }

  ngOnInit(): void {
    this.input.valueChanges.pipe(
      map(value => this.toTitleCase(isString(value) ? value : value?.name)),
      distinctUntilChanged(),
      untilDestroy(this)
    ).subscribe(value => this.onChange(value));
  }

  ngOnDestroy(): void { }

  writeValue(speciesName: string): void {
    this.input.setValue(speciesName);
  }

  onChange = (_) => { };

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  displayFn(species: any): string {
    return species?.name ? species.name : species;
  }

  addSpecies(name: string) {
    this.speciesService.addSpecies(name);
  }

  onChipTap(species: Species, event) {
    if (event.target.localName === 'mat-chip') {
      this.input.patchValue(species);
      this.speciesService.increaseFrequency(species);
    }
  }

  isSelected(species: Species): boolean {
    const value = this.input.value;
    if (isString(value)) {
      return species.name.toLowerCase() === value.toLowerCase();
    }
    return species.name.toLowerCase() === value?.value.toLowerCase();
  }

  onChipPress(species) {
    if (!species.default) {
      this.showRemove$.next(species.value);
    }
  }

  showRemove(species): boolean {
    return species.value === this.showRemove$.getValue();
  }

  removeSpecies(species) {
    this.speciesService.removeSpecies(species);
  }

  getShowAddSpecies(): Observable<boolean> {
    return combineLatest([
      this.input.valueChanges.pipe(map(value => isString(value) ? value : value?.name)),
      this.species$
    ]).pipe(
      map(([value, species]) => {
        if (!value || !this.checkIfValueExists(value, species)) {
          return false;
        }
        return true;
      })
    );
  }

  checkIfValueExists(value: string, species: Species[]): boolean {
    return !species.find(s => s.name.toLowerCase() === value?.toLowerCase());
  }

  private getFilteredSpecies(): Observable<Species[]> {
    return this.input.valueChanges.pipe(
      distinctUntilChanged(),
      startWith(''),
      filter(searchTerm => isString(searchTerm) || isNil(searchTerm)),
      switchMap(searchTerm => this.species$.pipe(
        map(species => species.filter(s => s?.name?.toLowerCase().includes(searchTerm.toLowerCase())))
      )),
      shareReplay(1)
    );
  }

  toTitleCase(string: string): string {
    return startCase(toLower(string));
  }

}
